import { tr } from "../i18n";
const PREFIX = "erp.colFreeze:";
const topCols = (exp) => exp.store?.states?._columns?.value || [];
const nameOf = (c) => c.label || c.property || "";
function keyOf(exp) {
    return PREFIX + location.pathname + "|" + topCols(exp).map((c) => nameOf(c) || c.type || "").join(",");
}
const EMPTY = { to: null, only: [] };
function load(exp) {
    let raw = null;
    try {
        raw = localStorage.getItem(keyOf(exp));
    }
    catch {
        return { ...EMPTY };
    }
    if (!raw)
        return { ...EMPTY };
    try {
        const j = JSON.parse(raw);
        if (j && typeof j === "object")
            return { to: j.to ?? null, only: Array.isArray(j.only) ? j.only.map(String) : [] };
    }
    catch { /* 老格式:只存了「冻结到」的列名 */ }
    return { to: raw, only: [] };
}
function save(exp, st) {
    try {
        if (!st.to && !st.only.length)
            localStorage.removeItem(keyOf(exp));
        else
            localStorage.setItem(keyOf(exp), JSON.stringify(st));
    }
    catch { /* 隐私模式 / 存不了就只管这一次 */ }
}
function contains(parent, target) {
    return (parent.children || []).some((c) => c === target || contains(c, target));
}
const LEAD_TYPES = new Set(["selection", "index", "expand"]);
/** 按记住的状态冻结 · 列名对不上(换了语言 / 列改了)的忽略 · 空状态 = 恢复页面原来的冻结 */
function apply(exp, st) {
    const cols = topCols(exp);
    if (!cols.length)
        return;
    for (const c of cols)
        if (c.__origFixed === undefined)
            c.__origFixed = c.fixed ?? false;
    const idx = st.to ? cols.findIndex((c) => nameOf(c) === st.to) : -1;
    const only = new Set(st.only);
    const anyOnly = cols.some((c) => only.has(nameOf(c)));
    let lead = true; // 最前面连着的勾选框 / 序号列:只冻结时跟着固定,免得冻结的列跑到勾选框前面
    cols.forEach((c, i) => {
        const isLead = lead && !!c.type && LEAD_TYPES.has(c.type);
        if (!isLead)
            lead = false;
        if (c.__origFixed === "right")
            return;
        const freeze = (idx >= 0 && i <= idx) || only.has(nameOf(c)) || (anyOnly && isLead);
        c.fixed = freeze ? "left" : c.__origFixed;
    });
    exp.store.updateColumns();
    exp.store.scheduleLayout?.();
    exp.doLayout?.();
}
function isFrozenByUser(exp) {
    return topCols(exp).some((c) => c.fixed === "left" && !(c.__origFixed === "left" || c.__origFixed === true));
}
// ── 右键小菜单 ──
let menu = null;
// 搜索框还没生效的输入(防抖中)· 点外面关掉搜索框 = 马上按现在输入的筛 · 回车 / Esc 自己处理完会先清掉它
let pendingSearch = null;
function closeMenu() {
    if (pendingSearch) {
        window.clearTimeout(pendingSearch.timer);
        const f = pendingSearch.flush;
        pendingSearch = null;
        f();
    }
    menu?.remove();
    menu = null;
    document.removeEventListener("mousedown", onDocDown, true);
    document.removeEventListener("keydown", onKey, true);
    window.removeEventListener("scroll", closeMenu, true);
}
function onDocDown(e) { if (menu && !menu.contains(e.target))
    closeMenu(); }
function onKey(e) { if (e.key === "Escape")
    closeMenu(); }
function openMenu(x, y, items) {
    closeMenu();
    menu = document.createElement("div");
    menu.className = "erp-col-freeze-menu";
    for (const it of items) {
        const b = document.createElement("div");
        b.className = "erp-col-freeze-item";
        b.textContent = it.label;
        b.onclick = () => { closeMenu(); it.run(); };
        menu.appendChild(b);
    }
    document.body.appendChild(menu);
    const r = menu.getBoundingClientRect();
    menu.style.left = `${Math.min(x, window.innerWidth - r.width - 8)}px`;
    menu.style.top = `${Math.min(y, window.innerHeight - r.height - 8)}px`;
    document.addEventListener("mousedown", onDocDown, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", closeMenu, true);
}
const servers = new WeakMap();
export function registerServerColSearch(root, cfg) {
    servers.set(root, cfg);
    return () => { if (servers.get(root) === cfg)
        servers.delete(root); };
}
function serverKeyOf(root, label) {
    try {
        return servers.get(root)?.fields()[label];
    }
    catch {
        return undefined;
    }
}
const searches = new WeakMap();
function clearSearch(root) {
    const s = searches.get(root);
    searches.delete(root);
    applySearch(root);
    if (s?.server)
        void servers.get(root)?.clear();
}
async function runServer(root, colId, label, key, q) {
    if (!q.trim()) {
        clearSearch(root);
        return;
    }
    const s = { colId, label, q: q.trim(), server: { key, busy: true } };
    searches.set(root, s);
    applySearch(root);
    try {
        await servers.get(root)?.search(key, q.trim());
    }
    catch { /* 页面自己会提示错误 */ }
    if (searches.get(root) === s) {
        s.server.busy = false;
        applySearch(root);
    }
}
function bodyRows(root) {
    return Array.from(root.querySelectorAll(".el-table__body-wrapper tbody > tr"));
}
function applySearch(root) {
    const s = searches.get(root);
    const rows = bodyRows(root);
    let chip = root.querySelector(":scope > .erp-col-search-chip");
    const unhideAll = () => rows.forEach((tr) => { if (tr.dataset.erpHidden) {
        tr.style.display = "";
        delete tr.dataset.erpHidden;
    } });
    if (!s || !s.q.trim()) {
        unhideAll();
        chip?.remove();
        return;
    }
    const makeChip = (text) => {
        if (!chip) {
            chip = document.createElement("div");
            chip.className = "erp-col-search-chip";
            root.appendChild(chip);
        }
        chip.textContent = "";
        const txt = document.createElement("span");
        txt.textContent = text;
        const x = document.createElement("span");
        x.className = "erp-col-search-x";
        x.textContent = "✕";
        x.title = tr("清除搜索");
        x.onclick = () => clearSearch(root);
        chip.append(txt, x);
    };
    if (s.server) { // 查全部数据:行是后端筛好的,这里只挂提示(总条数看页面自己的分页)
        unhideAll();
        makeChip(s.server.busy ? tr("正在全部数据里查「{col}」含「{q}」…", { col: s.label, q: s.q })
            : tr("全部数据里「{col}」含「{q}」", { col: s.label, q: s.q }));
        return;
    }
    const q = s.q.trim().toLowerCase();
    let total = 0, shown = 0;
    for (const tr of rows) {
        const td = tr.querySelector(`td.${CSS.escape(s.colId)}`);
        if (!td)
            continue; // 展开行 / 合并掉的行:不管
        total += 1;
        const hit = (td.innerText || "").toLowerCase().includes(q);
        if (hit)
            shown += 1;
        if (hit && tr.dataset.erpHidden) {
            tr.style.display = "";
            delete tr.dataset.erpHidden;
        }
        if (!hit && !tr.dataset.erpHidden) {
            tr.style.display = "none";
            tr.dataset.erpHidden = "1";
        }
    }
    makeChip(shown
        ? tr("「{col}」含「{q}」· 本页 {n}/{m} 行", { col: s.label, q: s.q.trim(), n: shown, m: total })
        : tr("本页没有「{col}」含「{q}」的行", { col: s.label, q: s.q.trim() }));
}
function openSearch(x, y, root, colId, label) {
    closeMenu();
    const old = searches.get(root);
    const key = serverKeyOf(root, label);
    menu = document.createElement("div");
    menu.className = "erp-col-freeze-menu erp-col-search-box";
    const title = document.createElement("div");
    title.className = "erp-col-search-title";
    title.textContent = key ? tr("搜索「{col}」这一列(查全部数据)", { col: label }) : tr("搜索「{col}」这一列(只筛本页)", { col: label });
    const input = document.createElement("input");
    input.className = "erp-col-search-input";
    input.placeholder = key ? tr("输入要找的内容 · 回车查 · Esc 清除") : tr("输入要找的内容 · 回车确定 · Esc 清除");
    input.value = old && old.colId === colId ? old.q : "";
    const start = input.value;
    const run = key
        ? () => { void runServer(root, colId, label, key, input.value); }
        : () => { searches.set(root, { colId, label, q: input.value }); applySearch(root); };
    const cancelPending = () => { if (pendingSearch) {
        window.clearTimeout(pendingSearch.timer);
        pendingSearch = null;
    } };
    input.oninput = () => {
        cancelPending();
        if (key) { // 查全部数据要发请求:不边输边查,回车 / 点外面才查(没改就不查)
            pendingSearch = input.value !== start ? { timer: 0, flush: run } : null;
            return;
        }
        const timer = window.setTimeout(() => { pendingSearch = null; run(); }, 150);
        pendingSearch = { timer, flush: run };
    };
    input.onkeydown = (ev) => {
        if (ev.key === "Enter") {
            cancelPending();
            run();
            closeMenu();
        }
        if (ev.key === "Escape") {
            ev.stopPropagation();
            cancelPending();
            clearSearch(root);
            closeMenu();
        }
    };
    menu.append(title, input);
    document.body.appendChild(menu);
    const r = menu.getBoundingClientRect();
    menu.style.left = `${Math.min(x, window.innerWidth - r.width - 8)}px`;
    menu.style.top = `${Math.min(y, window.innerHeight - r.height - 8)}px`;
    document.addEventListener("mousedown", onDocDown, true);
    window.addEventListener("scroll", closeMenu, true);
    input.focus();
    input.select();
}
function onContextMenu(e, exp, root) {
    const th = e.target?.closest?.("th");
    if (!th || !root.contains(th) || !th.closest(".el-table__header"))
        return; // 只管表头,表格内容照常是浏览器菜单
    const leaf = exp.store?.states?.columns?.value || [];
    const col = leaf.find((c) => th.classList.contains(c.id));
    if (!col)
        return;
    const top = topCols(exp).find((c) => c === col || contains(c, col));
    if (!top)
        return;
    const rightFixed = top.__origFixed === "right" || top.fixed === "right"; // 固定在右边的列(状态 / 操作)只能搜,不能冻结
    const label = nameOf(top);
    const st = load(exp);
    const set = (next) => { save(exp, next); apply(exp, next); };
    const items = [];
    const leafLabel = nameOf(col);
    if (leafLabel && !(col.type && LEAD_TYPES.has(col.type)) && !(rightFixed && leafLabel === tr("操作"))) { // 普通列 type 是 "default" · 勾选框 / 序号 / 操作列不搜
        const all = !!serverKeyOf(root, leafLabel);
        items.push({ label: all ? tr("搜索「{col}」这一列(查全部数据)", { col: leafLabel }) : tr("搜索「{col}」这一列", { col: leafLabel }),
            run: () => openSearch(e.clientX, e.clientY, root, col.id, leafLabel) });
    }
    if (searches.get(root)?.q.trim())
        items.push({ label: tr("清除搜索"), run: () => clearSearch(root) });
    if (label && !rightFixed) {
        items.push({ label: tr("冻结到「{col}」(这列和左边的列不随滚动)", { col: label }),
            run: () => set({ to: label, only: st.only.filter((x) => x !== label) }) });
        if (st.only.includes(label)) {
            items.push({ label: tr("取消冻结「{col}」", { col: label }), run: () => set({ ...st, only: st.only.filter((x) => x !== label) }) });
        }
        else if (top.fixed !== "left") {
            items.push({ label: tr("只冻结「{col}」(挪到最左边,其他列照常滚动)", { col: label }),
                run: () => set({ ...st, only: [...st.only, label] }) });
        }
    }
    if (isFrozenByUser(exp))
        items.push({ label: tr("取消全部冻结"), run: () => set({ ...EMPTY }) });
    if (!items.length)
        return;
    e.preventDefault();
    openMenu(e.clientX, e.clientY, items);
}
export default {
    install(app) {
        const style = document.createElement("style");
        style.textContent = `
.erp-col-freeze-menu { position: fixed; z-index: 4000; min-width: 160px; padding: 4px 0; font-size: 13px;
  background: var(--el-bg-color-overlay, #fff); color: var(--el-text-color-regular, #333);
  border: 1px solid var(--el-border-color-light, #e4e7ed); border-radius: 4px; box-shadow: var(--el-box-shadow-light); }
.erp-col-freeze-item { padding: 6px 14px; cursor: pointer; white-space: nowrap; }
.erp-col-freeze-item:hover { background: var(--el-fill-color-light, #f5f7fa); color: var(--el-color-primary); }
.erp-col-search-box { padding: 8px 10px; min-width: 260px; }
.erp-col-search-title { font-size: 12px; color: var(--el-text-color-secondary, #909399); margin-bottom: 6px; }
.erp-col-search-input { width: 100%; box-sizing: border-box; padding: 5px 8px; font-size: 13px; outline: none;
  border: 1px solid var(--el-border-color, #dcdfe6); border-radius: 4px; background: var(--el-fill-color-blank, #fff);
  color: var(--el-text-color-regular, #333); }
.erp-col-search-input:focus { border-color: var(--el-color-primary); }
.erp-col-search-chip { position: absolute; top: 4px; right: 8px; z-index: 30; display: flex; gap: 8px; align-items: center;
  padding: 2px 6px 2px 10px; font-size: 12px; border-radius: 10px; pointer-events: auto;
  background: var(--el-color-warning-light-9, #fdf6ec); color: var(--el-color-warning-dark-2, #b88230);
  border: 1px solid var(--el-color-warning-light-5, #f3d19e); box-shadow: var(--el-box-shadow-lighter); }
.erp-col-search-x { cursor: pointer; padding: 0 4px; }
.erp-col-search-x:hover { color: var(--el-color-danger); }`;
        document.head.appendChild(style);
        app.mixin({
            mounted() {
                if (this.$options?.name !== "ElTable")
                    return;
                try {
                    const exp = this.$?.exposed;
                    const root = this.$el;
                    if (!exp?.store || !root?.addEventListener)
                        return;
                    const handler = (e) => onContextMenu(e, exp, root);
                    root.addEventListener("contextmenu", handler);
                    const restore = () => { const st = load(exp); if (st.to || st.only.length)
                        apply(exp, st); };
                    restore();
                    // 列有 v-if 时会重建 → 再按记住的冻结一次
                    const stop = this.$watch(() => topCols(exp).length, () => restore());
                    // 搜索这一列:行重新渲染(翻页 / 刷新 / 排序)后再筛一次 · 只看增删行,不看属性(藏行改的是 style,不会自己触发自己)
                    let raf = 0;
                    const mo = new MutationObserver(() => {
                        if (!searches.get(root)?.q.trim() || raf)
                            return;
                        raf = requestAnimationFrame(() => { raf = 0; applySearch(root); });
                    });
                    const body = root.querySelector(".el-table__body-wrapper");
                    if (body)
                        mo.observe(body, { childList: true, subtree: true, characterData: true });
                    this.__colFreezeOff = () => { root.removeEventListener("contextmenu", handler); stop?.(); mo.disconnect(); searches.delete(root); };
                }
                catch { /* 冻结只是方便,出错也不能影响表格本身 */ }
            },
            beforeUnmount() {
                if (this.$options?.name === "ElTable")
                    this.__colFreezeOff?.();
            },
        });
    },
};
//# sourceMappingURL=tableFreeze.js.map